import React, { useState, useEffect } from 'react';
import { Row, Col, Tab } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useSetTab } from '../../services/hooks';
import { HRDashboardStyled } from '../../components';
import { SimpleCenteredModal } from '../../components/Utils/Modal';
import { humanResourcesRequest } from '../../requests/dashboards';
import { camelCaseEmptyStringRecursive } from '../../services/utils';
import AdvancePaymentColumns from '../Shared/AdvancePayment/approveColumns';
import AdvancePaymentDataTable from '../Shared/AdvancePayment/AdvancePaymentDataTable';
import EmployeeDataTable from '../Shared/Employee/EmployeeDataTable';
import ExpireContractColumns from './ExpireContractColumns';
import LoanColumns from '../Shared/Loan/approveColumns';
import LoanDataTable from '../Shared/Loan/LoanDataTable';
import PendingColumns from './PendingContractsColumns';
import PermissionColumns from '../Shared/Permission/approveColumns';
import PermissionDataTable from '../Shared/Permission/PermissionDataTable';
import VacationColumns from '../Shared/Vacation/approveColumns';
import VacationDataTable from '../Shared/Vacation/VacationDataTable';
import WorkflowColumns from './WorkflowColumns';
import WorkflowRequestDataTable from '../WorkflowRequest/WorkflowRequestDataTable';

const styles = {
  modalContainer: { minHeight: '350px' }
};

const HRDashboard = () => {
  const location = useLocation();
  const [cards, setCards] = useState({});
  const [maxHeight, setMaxHeight] = useState(250);
  const [filter, setFilter] = useState('jobManagement');
  const [onRequest, setOnRequest] = useState(true);
  const [key, setKey] = useSetTab('contracts', location);
  const { advanceSettings } = useSelector(state => state.utils);
  const [moreData, setMoreData] = useState(false);
  const legalHolidayEnabled =
    advanceSettings.filter(asItem => asItem.code === 'legal_holiday')[0]?.textValue === 'true';
  const progressiveHolidayEnabled =
    advanceSettings.filter(asItem => asItem.code === 'progressive_holiday')[0]?.textValue === 'true';
  const additionalDaysEnabled =
    advanceSettings.filter(asItem => asItem.code === 'additional_days')[0]?.textValue === 'true';
  const loanCard = advanceSettings?.filter(asItem => asItem.code === 'loan_request')[0]?.textValue === 'true';
  const apCard = advanceSettings?.filter(asItem => asItem.code === 'advance_payment_request')[0]?.textValue === 'true';
  const vacationVisible = legalHolidayEnabled || progressiveHolidayEnabled || additionalDaysEnabled;
  const companyLeave = advanceSettings?.filter(
    asItem => asItem.code === 'paid_leave' || asItem.code === 'without_paid_leave'
  );
  const PermissionCard = companyLeave.some(x => x.textValue === 'true');
  const dispatch = useDispatch();
  const [tempExpireDate, setTempExpireDate] = useState(0);
  const [numberRenders, setNumberReders] = useState(0);
  let expireDate = 0;
  const [modalBody, setModalBody] = useState('');
  const [modalShow, setModalShow] = useState(false);

  const handleClick = (value, TopHeaderColor) => {
    document.documentElement.style.setProperty('--TopHeaderColor', TopHeaderColor);
    setKey(value);
    setModalShow(true);
    setModalBody(
      <Tab.Container id="dashboard-tabs" activeKey={value} onSelect={value} mountOnEnter>
        <Tab.Content style={styles.modalContainer}>
          <Tab.Pane eventKey="contracts" style={styles.modalContainer}>
            {expireDate > 0 && (
              <EmployeeDataTable
                columns={ExpireContractColumns}
                clickRoute="/employees"
                customParams={{ to_expire: expireDate, active_staffing: true, active: true }}
                refreshDashboard={() => setMoreData(!moreData)}
                removeExpireFilter
                importContract
              />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="pending_employees" style={styles.modalContainer}>
            <EmployeeDataTable
              columns={PendingColumns}
              customParams={{ pending_contracts: true, active_staffing: true, active: true }}
              clickRoute="/employees"
              refreshDashboard={() => setMoreData(!moreData)}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="workflows" style={styles.modalContainer}>
            <WorkflowRequestDataTable
              customFilter={{ status: 'pending' }}
              columns={WorkflowColumns}
              refreshDashboard={() => setMoreData(!moreData)}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="vacations" style={styles.modalContainer}>
            {vacationVisible && (
              <VacationDataTable
                columns={VacationColumns}
                customParams={{
                  status: 'pending',
                  filter_vacation_exception:
                    'administrative_days,legal_openning_balance,progressive_openning_balance,additional_days_openning_balance,administrative_days_openning_balance'
                }}
                moreData={moreData}
                setMoreData={setMoreData}
              />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="loans" style={styles.modalContainer}>
            {loanCard && (
              <LoanDataTable
                columns={LoanColumns}
                customParams={{ status: 'pending' }}
                moreData={moreData}
                setMoreData={setMoreData}
                withImport
              />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="advance_payments" style={styles.modalContainer}>
            {apCard && (
              <AdvancePaymentDataTable
                columns={AdvancePaymentColumns}
                customParams={{ status: 'pending' }}
                moreData={moreData}
                setMoreData={setMoreData}
                withImport
              />
            )}
          </Tab.Pane>
          <Tab.Pane eventKey="permissions" style={styles.modalContainer}>
            {PermissionCard && (
              <PermissionDataTable
                columns={PermissionColumns}
                customParams={{ status: 'pending' }}
                moreData={moreData}
                setMoreData={setMoreData}
              />
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    );
  };

  const dataTableHead = () => {
    switch (key) {
      case 'employees':
        return 'Trabajadores';
      case 'contracts':
        return 'Contratos por Vencer';
      case 'pending_employees':
        return 'Fichas por Completar';
      case 'workflows':
        return 'Documentos pendientes de Firma';
      case 'vacations':
        return 'Solicitudes de Vacaciones Pendientes';
      case 'loans':
        return 'Solicitudes de Préstamos Pendientes';
      case 'advance_payments':
        return 'Solicitudes de Anticipos Pendientes';
      case 'permissions':
        return 'Solicitudes de Permisos Pendientes';
      default:
        return '';
    }
  };

  const fetchCardData = () => {
    const request = async () => {
      humanResourcesRequest({
        dispatch,
        successCallback: response => {
          setCards(camelCaseEmptyStringRecursive(response.data));
          setOnRequest(false);
        }
      });
    };
    request();
  };

  useEffect(fetchCardData, [moreData]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const localExpireDate = advanceSettings?.filter(asItem => asItem.code === 'expire_days')[0]?.integerValue || 0;
    setTempExpireDate(localExpireDate);
    setNumberReders(old => old + 1);
  }, [advanceSettings]);

  // esto es por si no existe ningun settins tome por defecto 5 y renderize el componente
  if (
    (numberRenders >= 2 && tempExpireDate === 0) ||
    (numberRenders === 1 && tempExpireDate === 0 && advanceSettings.length > 0)
  ) {
    expireDate = 5;
  } else {
    expireDate = tempExpireDate;
  }

  return (
    <>
      <div className="MainDivCard" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="col-sm-12">
          <Row>
            <Col md={6} xl={4} xs={12}>
              <p style={{ fontSize: '20px', fontWeight: 'bold' }}>
                <span style={{ color: '#2bb48f' }}>Dashboard</span> Gestión de Personas
              </p>
            </Col>
          </Row>
        </div>
        <div className="col-sm-12 MainDivHrCard">
          <Row>
            <Col className="mt-2" md={6} xl={3} xs={12}>
              <HRDashboardStyled
                title={cards.fullStaff}
                label="Colaboradores"
                TopHeaderColor="#fff"
                subtitle="Registrados"
                TypeCard="Colaborators"
                CardName="Colaborators"
                Downloadable
                onRequest={onRequest}
              />
            </Col>
            <Col className="mt-2" md={6} xl={3} xs={12}>
              <HRDashboardStyled
                title={cards.undefinedContracts}
                label="Contratos Indefinido"
                TopHeaderColor="#fff"
                GeneralColor="#013220"
                subtitle={`${cards.percentUndefinedContracts} % De la dotación`}
                subtitle2="Colaboradores"
                TypeCard="OnlyInformation"
                CardName="UndefinedContracts"
                Downloadable
                onRequest={onRequest}
              />
            </Col>
            <Col className="mt-2" md={6} xl={3} xs={12}>
              <HRDashboardStyled
                title={cards.fixedTermContracts}
                label="Contratos a Plazo Fijo"
                TopHeaderColor="#fff"
                GeneralColor="#ffa600"
                subtitle={`${cards.percentFixedTermContracts} % De la dotación`}
                subtitle2="Colaboradores"
                TypeCard="OnlyInformation"
                CardName="FixedTermContracts"
                onRequest={onRequest}
                Downloadable
                labelFontSize="12px"
              />
            </Col>
            <Col className="mt-2" md={6} xl={3} xs={12}>
              <HRDashboardStyled
                title={cards.pendingEmployees}
                subtitle={`${cards.percentPendingEmployees} % De la dotación`}
                subtitle2="Colaboradores"
                fontSizeSubtitle="12px"
                label="Fichas por Completar"
                TopHeaderColor="#fff"
                GeneralColor="#b40000"
                TypeCard="SeeMore"
                CardName="PendingEmployees"
                onClick={() => handleClick('pending_employees', '#b40000')}
                onRequest={onRequest}
                labelFontSize="12px"
                ViewMore
              />
            </Col>
          </Row>

          <Row>
            <Col className="mt-2" md={8} xl={6} xs={12}>
              <HRDashboardStyled
                title={cards.monthlyContractSummary}
                label="Contratados Vs Finiquitados (Últimos 6 meses)"
                TopHeaderColor="#fff"
                TypeCard="MonthlyContractSummary"
                CardName="MonthlyContractSummary"
                onRequest={onRequest}
              />
            </Col>
            <Col className="mt-2" md={8} xl={6} xs={12}>
              <Row>
                <Col md={6} xl={6} xs={12}>
                  <HRDashboardStyled
                    title={cards.groupByAgeAndGender}
                    label="Género"
                    TopHeaderColor="#fff"
                    subtitle={cards.groupByGender}
                    TypeCard="AgeDistribution"
                    CardName="AgeDistribution"
                    onRequest={onRequest}
                  />
                </Col>
                <Col md={8} xl={6} xs={12}>
                  <HRDashboardStyled
                    title={cards.groupByNacionality}
                    label="Extranjeros Vs Chilenos"
                    TopHeaderColor="#fff"
                    GeneralColor="#b40000"
                    TypeCard="ChileansNoChileansEmployees"
                    CardName="ChileansNoChileansEmployees"
                    onRequest={onRequest}
                    labelFontSize="10px"
                  />
                </Col>
              </Row>

              <Row>
                <Col className="mt-2" md={6} xl={6} xs={12}>
                  <HRDashboardStyled
                    title={cards.expireContracts}
                    label=""
                    TopHeaderColor="#fff"
                    GeneralColor="#d3000d"
                    subtitle={`En los próximos ${expireDate} días`}
                    subtitle2="Contratos Por Vencer y Vencidos"
                    subtitle2FontSize="11px"
                    TypeCard="SeeMore"
                    CardName="ExpireContracts"
                    onClick={() => handleClick('contracts', '#b40000')}
                    onRequest={onRequest}
                    labelFontSize="11px"
                    ViewMore
                  />
                </Col>
                <Col className="mt-2" md={6} xl={6} xs={12}>
                  <HRDashboardStyled
                    title={cards.countDisability}
                    label=""
                    TopHeaderColor="#fff"
                    subtitle2="Discapacitados"
                    TypeCard="OnlyInformation"
                    CardName="Disability"
                    GeneralColor="#21653b"
                    onRequest={onRequest}
                    fontSizeSubtitle="14px"
                    Downloadable
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row>
            <Col className="mt-2" md={3} xl={3} xs={12}>
              <HRDashboardStyled
                title={cards.groupByAntiquityRange}
                label="Antigüedad"
                TopHeaderColor="#fff"
                TypeCard="GroupByAntiquityGraph"
                CardName="GroupByAntiquityGraph"
                GeneralColor="#b40000"
                onRequest={onRequest}
              />
            </Col>
            <Col className="mt-2" md={6} xl={6} xs={12}>
              <HRDashboardStyled
                title={cards.groupByAgeRange}
                label="Grupo Etario"
                TopHeaderColor="#fff"
                TypeCard="GroupByAgeDistribution"
                CardName="GroupByAgeDistribution"
                GeneralColor="#b40000"
                onRequest={onRequest}
              />
            </Col>
            <Col className="mt-2" md={3} xl={3} xs={12}>
              <HRDashboardStyled
                title={cards.groupByCivilState}
                label="Estado Civil"
                TopHeaderColor="#fff"
                TypeCard="GroupByCivilState"
                CardName="GroupByCivilState"
                GeneralColor="#b40000"
                onRequest={onRequest}
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-4 mt-2" md={9} xl={9} xs={12}>
              <HRDashboardStyled
                title={cards}
                label=""
                TopHeaderColor="#fff"
                GeneralColor="#b40000"
                TypeCard="DinamicGraph"
                onRequest={onRequest}
                maxHeight={maxHeight}
                setMaxHeight={setMaxHeight}
                filter={filter}
                setFilter={setFilter}
              />
            </Col>
            <Col className="mb-4 mt-2" md={3} xl={3} xs={12}>
              {maxHeight !== 250 && (
                <HRDashboardStyled
                  title={cards.groupByRegion}
                  label="Lugar de Prestación de Servicios"
                  TopHeaderColor="#fff"
                  GeneralColor="#b40000"
                  AnyList={cards.groupByCommune}
                  TypeCard="EmployeeDistribution"
                  CardName="EmployeeDistribution"
                  onRequest={onRequest}
                  maxHeight={maxHeight}
                  setMaxHeight={setMaxHeight}
                />
              )}
            </Col>
          </Row>

          <SimpleCenteredModal
            headerClass="TopHeaderStyle"
            size="xl"
            title={dataTableHead()}
            body={modalBody}
            show={modalShow}
            onHide={() => setModalShow(false)}
          />
        </div>
      </div>
    </>
  );
};

export default HRDashboard;
